import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Touch-friendly checkbox component with:
 * - Extended touch target (44x44px minimum per WCAG 2.5.5)
 * - Visual feedback for touch interactions
 * - Larger visual indicator for better usability
 */

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      // Base styles - increased from h-4 w-4 to h-5 w-5 for better touch
      "peer h-5 w-5 shrink-0 rounded-sm border border-primary",
      // Focus styles
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      // Disabled states
      "disabled:cursor-not-allowed disabled:opacity-50",
      // Checked states
      "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      // Touch-friendly enhancements
      "touch-action-manipulation select-none",
      // Extended touch target using relative positioning
      "relative",
      // Active state feedback
      "active:scale-95 transition-transform duration-100",
      className
    )}
    {...props}
  >
    {/* Extended touch target - invisible but captures touch/click */}
    <span
      className="absolute -inset-2 min-w-[44px] min-h-[44px]"
      style={{
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: '44px',
        minHeight: '44px',
      }}
    />
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
